import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { CountryFlagLabel } from "@/components/Common/CountryFlagLabel";
import { HARDWARE_MAP } from "@/utils/mapping";
import { ConfirmModal } from "@/components/Common/ConfirmModal/ConfirmModal";
import { Loader } from "@/components/Common/Loader/Loader";

export type CreateFormControlBarSummary = {
  processorLabel: string;
  gpusPerHardware: number | string;
  hourlyPrice: string;
  vramPerCard: number;
  storage?: number;
  supplier?: "internal" | "external";
  locationCode: string;
  locationName: string;
};

function formatSupplier(supplier: "internal" | "external" | string | undefined): string {
  if (!supplier) return "-";
  return supplier === "external" ? "Partner" : "IO";
}

type CreateFormControlBarProps = {
  backLabel?: string;
  onBack?: () => void;
  continueLabel?: string;
  onContinue: () => void;
  isContinueDisabled?: boolean;
  isLoading?: boolean;
  /** Hardware summary shown in the center on desktop. Hidden on mobile. */
  summary?: CreateFormControlBarSummary;
  /** Which detail field to show between VRAM and location. Containers use supplier; VMs use storage. */
  summaryDetailField?: "storage" | "supplier";
  /** When payment is in progress, show a full-page overlay and cancel control in the footer. */
  isPaymentPending?: boolean;
  onConfirmCancelPayment?: () => void;
  className?: string;
};

function formatStorage(storageGb: number | undefined | null): string {
  if (storageGb == null || isNaN(storageGb) || storageGb <= 0) return "-";
  if (storageGb >= 1000) {
    const tb = storageGb / 1000;
    return `${tb % 1 === 0 ? tb : tb.toFixed(1)} TB`;
  }
  return `${storageGb} GB`;
}

/** Matches the Figma divider: a 1.25px-wide container with a rotated 36px line inside */
const VerticalDivider: React.FC = () => (
  <div className="flex h-[36px] w-[1.25px] shrink-0 items-center justify-center">
    <div className="-rotate-90 flex-none">
      <div className="h-[1.25px] w-[36px] bg-border" />
    </div>
  </div>
);

type SummaryPillProps = {
  summary: CreateFormControlBarSummary;
  summaryDetailField: "storage" | "supplier";
  ManufacturerIcon?: React.ComponentType<{ className?: string }>;
  manufacturerColor?: string;
  fullWidth?: boolean;
};

const SummaryPill: React.FC<SummaryPillProps> = ({
  summary,
  summaryDetailField,
  ManufacturerIcon,
  manufacturerColor,
  fullWidth = false
}) => (
  <div
    className={cn(
      "flex h-9 items-center rounded-[8px] border-[1.25px] border-border px-[12px]",
      fullWidth ? "w-full min-w-0" : "max-w-full"
    )}
  >
    <div
      className={cn(
        "flex h-full min-w-0 flex-nowrap items-center gap-x-3",
        fullWidth && "w-full min-w-0 overflow-x-auto"
      )}
    >
      <div className="flex items-center gap-[8px]">
        {ManufacturerIcon && (
          <div className="relative h-[16px] w-[16px] shrink-0">
            <ManufacturerIcon className={cn("h-[16px] w-[16px]", manufacturerColor)} />
          </div>
        )}
        <span className="whitespace-nowrap text-[12px] font-medium leading-[16px] text-foreground">
          {summary.processorLabel}
        </span>
        <div className="flex h-[20px] shrink-0 items-center justify-center rounded-[6px] border-[1.25px] border-border px-[8px]">
          <span className="whitespace-nowrap text-[12px] font-medium leading-[16px] text-foreground">
            x{summary.gpusPerHardware}
          </span>
        </div>
      </div>

      <VerticalDivider />

      <div className="flex items-center gap-[12px] whitespace-nowrap text-[12px] font-medium leading-[16px]">
        <span className="text-muted-foreground">Price</span>
        <span className="text-foreground">{summary.hourlyPrice}</span>
      </div>

      <VerticalDivider />

      <div className="flex items-center gap-[12px] whitespace-nowrap text-[12px] font-medium leading-[16px]">
        <span className="text-muted-foreground">VRAM</span>
        <span className="text-foreground">
          {summary.vramPerCard} GB x{summary.gpusPerHardware}
        </span>
      </div>

      <VerticalDivider />

      <div className="flex items-center gap-[12px] whitespace-nowrap text-[12px] font-medium leading-[16px]">
        <span className="text-muted-foreground">
          {summaryDetailField === "supplier" ? "Supplier" : "Storage"}
        </span>
        <span className="text-foreground">
          {summaryDetailField === "supplier"
            ? formatSupplier(summary.supplier)
            : formatStorage(summary.storage)}
        </span>
      </div>

      <VerticalDivider />

      <CountryFlagLabel
        code={summary.locationCode}
        name={summary.locationName}
        nameClassName="whitespace-nowrap text-[12px] font-medium leading-[16px] text-foreground"
      />
    </div>
  </div>
);

export const CreateFormControlBar: React.FC<CreateFormControlBarProps> = ({
  backLabel = "Back",
  onBack,
  continueLabel = "Continue",
  onContinue,
  isContinueDisabled = false,
  isLoading = false,
  summary,
  summaryDetailField = "storage",
  isPaymentPending = false,
  onConfirmCancelPayment,
  className
}) => {
  const [isCancelPaymentModalOpen, setIsCancelPaymentModalOpen] = useState(false);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  const nvidiaManufacturer = HARDWARE_MAP["nvidia"];
  const ManufacturerIcon = nvidiaManufacturer?.hardwareManufacturerIcon;

  useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  const actionButtons = isPaymentPending ? (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setIsCancelPaymentModalOpen(true)}
      >
        Cancel
      </Button>
      <Button type="button" size="sm" className="gap-2" disabled variant="standout">
        <Loader className="h-4 w-4" color="#ffffff" />
        {continueLabel}
      </Button>
    </div>
  ) : (
    <Button
      type="button"
      size="sm"
      className={cn(
        "shrink-0 gap-2",
        // Keep clickable when invalid so the parent can scroll to / show field errors.
        isContinueDisabled && "opacity-50"
      )}
      onClick={onContinue}
      disabled={isLoading}
      aria-disabled={isContinueDisabled || isLoading}
      variant="standout"
    >
      {continueLabel}
      <ChevronRight className="h-4 w-4" />
    </Button>
  );

  const controlBar = (
    <div
      className={cn(
        "flex w-full flex-col gap-2 border-t border-border bg-background px-6 py-2 lg:px-8",
        "min-h-[68px] 2xl:flex-row 2xl:items-center 2xl:justify-between 2xl:gap-4",
        isPaymentPending ? "fixed inset-x-0 bottom-0 z-50" : "sticky bottom-0 z-10",
        className
      )}
    >
      {/* Below 2xl: summary on top row */}
      {summary && (
        <div className="hidden w-full min-w-0 lg:block 2xl:hidden">
          <SummaryPill
            fullWidth
            summary={summary}
            summaryDetailField={summaryDetailField}
            ManufacturerIcon={ManufacturerIcon}
            manufacturerColor={nvidiaManufacturer.hardwareManufacturerColor}
          />
        </div>
      )}

      {/* Below 2xl: back + continue on second row; at 2xl+: back left, summary + continue right */}
      <div className="flex w-full items-center justify-between gap-2">
        <div className="shrink-0">
          {onBack && !isPaymentPending && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={onBack}
            >
              <ChevronLeft className="h-4 w-4" />
              {backLabel}
            </Button>
          )}
        </div>

        <div className="flex min-w-0 items-center gap-2">
          {summary && (
            <div className="hidden min-w-0 2xl:flex">
              <SummaryPill
                summary={summary}
                summaryDetailField={summaryDetailField}
                ManufacturerIcon={ManufacturerIcon}
                manufacturerColor={nvidiaManufacturer.hardwareManufacturerColor}
              />
            </div>
          )}
          {actionButtons}
        </div>
      </div>
    </div>
  );

  const paymentOverlay =
    portalRoot &&
    createPortal(
      <div className="fixed inset-0 z-40 bg-black/80" aria-hidden="true" />,
      portalRoot
    );

  const portaledControlBar =
    portalRoot && isPaymentPending ? createPortal(controlBar, portalRoot) : null;

  return (
    <>
      {isPaymentPending && paymentOverlay}
      {isPaymentPending ? (
        <>
          {portaledControlBar}
          <div className="min-h-[68px]" aria-hidden="true" />
        </>
      ) : (
        controlBar
      )}

      <ConfirmModal
        title="Are you sure you want to cancel your payment?"
        description="Your payment will not be processed. You can return and try again when you're ready."
        submitLabel="Yes, cancel payment"
        submitButtonVariant="error"
        isOpen={isCancelPaymentModalOpen}
        onSubmit={() => {
          onConfirmCancelPayment?.();
          setIsCancelPaymentModalOpen(false);
        }}
        onClose={() => setIsCancelPaymentModalOpen(false)}
      />
    </>
  );
};
